'use client';

import type { RoleValue } from '@vaults/ui/management/UseOrganizationAccessManagementHook';
import { useRolePrivileges } from '@core/ui/hooks/useRolePrivileges';
import { Spinner } from '@core/ui/primitives/spinner';

interface RolePrivilegesInfoProps {
  role: RoleValue;
  className?: string;
}

/**
 * Displays the privileges associated with a role.
 * Data is fetched dynamically from the server to reflect current privilege configuration.
 */
export function RolePrivilegesInfo({ role, className }: RolePrivilegesInfoProps) {
  const { loading, error, getPrivilegesForRole } = useRolePrivileges();
  const privileges = getPrivilegesForRole(role);

  if (loading) {
    return (
      <div className={className}>
        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
          <Spinner className='h-3 w-3' />
          <span>Loading privileges...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <p className='text-xs text-muted-foreground'>Unable to load privileges</p>
      </div>
    );
  }

  if (!privileges || privileges.length === 0) {
    return null;
  }

  // Split into two columns when there are more than 4 items
  const useColumns = privileges.length > 4;

  return (
    <div className={className}>
      <p className='text-xs font-medium text-muted-foreground mb-1'>
        This role can:
      </p>
      <ul
        className={`text-xs text-muted-foreground space-y-0.5 list-disc list-inside ${
          useColumns ? 'columns-2 gap-x-4' : ''
        }`}>
        {privileges.map((privilege) => (
          <li key={privilege.id} className={useColumns ? 'break-inside-avoid' : ''}>
            {privilege.description}
          </li>
        ))}
      </ul>
    </div>
  );
}
